import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchClient, clearClient, archiverClient, activerClient } from '../store/slices/clientsSlice';
import { toast } from 'react-toastify';
import api from '../services/api';
import {
  ArrowLeft, Pencil, UserX, RotateCcw, User, Phone, Briefcase,
  Calendar, FileText, Users, Activity,
  CheckCircle, Clock, AlertCircle, Upload, Trash2, Eye, Printer,
} from 'lucide-react';

const TYPE_DOCUMENT_LABELS = {
  cni:       "Carte Nationale d'Identité",
  passeport: 'Passeport',
  fiche_id:  "Fiche d'Identification",
  contrat:   'Contrat Signé',
  avenant:   'Avenant / Renouvellement',
  autre:     'Autre Document',
};

/* ── Helpers ── */
const fmt       = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const sexeLabel = (s) => s === 'masculin' ? '♂ Masculin' : s === 'feminin' ? '♀ Féminin' : '—';
const age = (d) => {
  if (!d) return null;
  const a = Math.floor((Date.now() - new Date(d).getTime()) / (1000 * 60 * 60 * 24 * 365.25));
  return `${a} ans`;
};
const anniversaire = (d) => {
  if (!d) return '—';
  const dn = new Date(d);
  return `${String(dn.getDate()).padStart(2,'0')}/${String(dn.getMonth()+1).padStart(2,'0')}`;
};

/* ── Styles compacts ── */
const S = {
  card:   { padding:'10px 14px', display:'flex', flexDirection:'column' },
  head:   { display:'flex', alignItems:'center', gap:7, paddingBottom:8,
            borderBottom:'1px solid #E5E7EB', marginBottom:10 },
  htitle: { fontSize:11, fontWeight:700, color:'#1F4E79', margin:0,
            textTransform:'uppercase', letterSpacing:'0.05em' },
  grid2:  { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 14px' },
};

function SHead({ icon: Icon, title, color = '#1F4E79' }) {
  return (
    <div style={S.head}>
      <Icon size={13} color={color}/>
      <span style={{ ...S.htitle, color }}>{title}</span>
    </div>
  );
}

function Field({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div>
      <div style={{ fontSize:10, fontWeight:600, color:'#9CA3AF', textTransform:'uppercase', letterSpacing:'0.04em' }}>{label}</div>
      <div style={{ fontSize:12, fontWeight:600, color:'#1F2937', marginTop:2 }}>{value}</div>
    </div>
  );
}

/* ── Onglets ── */
const TABS = [
  { id: 'fiche',     label: 'Fiche client' },
  { id: 'coachings', label: 'Coachings' },
  { id: 'documents', label: 'Documents' },
  { id: 'historique',label: 'Historique' },
];

export default function ClientDetailPage() {
  const { id }   = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { client, loading } = useSelector(s => s.clients);

  const [tab,        setTab]        = useState('fiche');
  const [historique, setHistorique] = useState(null);
  const [loadingHist,setLoadingHist]= useState(false);

  useEffect(() => {
    dispatch(fetchClient(id));
    return () => dispatch(clearClient());
  }, [dispatch, id]);

  /* Charger l'historique à la demande */
  useEffect(() => {
    if (tab === 'historique' && !historique && id) {
      setLoadingHist(true);
      import('../services/api').then(({ default: api }) => {
        api.get(`/clients/${id}/historique/`)
          .then(r => setHistorique(r.data))
          .catch(() => setHistorique({ coachings: [], notifications: [], documents: [] }))
          .finally(() => setLoadingHist(false));
      });
    }
  }, [tab, historique, id]);

  const handleArchiver = async () => {
    if (!window.confirm(`Archiver le client ${client.prenom_cli} ${client.nom_cli} ?`)) return;
    try {
      await dispatch(archiverClient(id)).unwrap();
      toast.success('Client archivé.');
      dispatch(fetchClient(id));
    } catch {
      toast.error('Erreur lors de l\'archivage.');
    }
  };

  const handleActiver = async () => {
    if (!window.confirm(`Réactiver le client ${client.prenom_cli} ${client.nom_cli} ?`)) return;
    try {
      await dispatch(activerClient(id)).unwrap();
      toast.success('Client réactivé.');
      dispatch(fetchClient(id));
    } catch {
      toast.error('Erreur lors de la réactivation.');
    }
  };

  /* ── Chargement ── */
  if (loading || !client) {
    return (
      <div className="spinner" style={{ marginTop: 80 }}>
        <div className="spinner-ring" /> Chargement de la fiche...
      </div>
    );
  }

  const statutColor = { actif: '#1E7145', inactif: '#ED7D31', archive: '#6B7280' };
  const statutBg    = { actif: '#E8F5E9',  inactif: '#FFF8E7',  archive: '#F5F5F5' };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* ── Print header ── */}
      <div className="print-header">
        <div>
          <div className="print-header-logo">NANCOZI CORPORATION — CRM</div>
          <div className="print-header-sub">Fiche Client — Document confidentiel</div>
        </div>
        <div className="print-header-date">
          Imprimé le {new Date().toLocaleDateString('fr-FR')}<br />
          {new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>

      {/* ── Synthèse print ── */}
      <div className="print-only" style={{ marginBottom: 10 }}>
        <div style={{
          background: '#1F4E79', color: '#FFFFFF', borderRadius: 6,
          padding: '8pt 12pt', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact',
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#FFFFFF', letterSpacing: 0.5 }}>
              {client.prenom_cli} {client.nom_cli}
            </div>
            <div style={{ fontSize: 9, color: '#CBD5E1', marginTop: 3 }}>
              {client.fonction || 'Client'} &nbsp;·&nbsp;
              <span style={{ color: '#86EFAC', fontWeight: 700 }}>{client.statut_cli?.toUpperCase()}</span>
              {client.commercial_ref_nom && ` · Réf. ${client.commercial_ref_nom}`}
            </div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 9, color: '#CBD5E1' }}>
            <div>Inscrit le {fmt(client.date_inscription_cli)}</div>
            <div style={{ marginTop: 5, display: 'flex', gap: 12, justifyContent: 'flex-end', color: '#FFFFFF' }}>
              <span>Coachings actifs : <strong>{client.nb_coachings?.actifs ?? 0}</strong></span>
              <span>Total : <strong>{client.nb_coachings?.total ?? 0}</strong></span>
              <span>Documents : <strong>{client.documents?.length ?? 0}</strong></span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Fixed header bar ── */}
      <div className="no-print" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 16px', borderBottom: '1px solid #E5E7EB',
        background: '#FFFFFF', flexShrink: 0, gap: 12, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="header-btn btn-secondary btn-sm"
            onClick={() => navigate('/clients')}
            style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <ArrowLeft size={14} /> Retour
          </button>
          <div style={{
            width: 38, height: 38, borderRadius: '50%', background: '#1F4E79',
            color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: 14, flexShrink: 0,
          }}>
            {client.prenom_cli?.[0]}{client.nom_cli?.[0]}
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1F2937', lineHeight: 1.2 }}>
              {client.prenom_cli} {client.nom_cli}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
              <span style={{
                background: statutBg[client.statut_cli] || '#F5F5F5',
                color: statutColor[client.statut_cli] || '#6B7280',
                fontSize: 10, fontWeight: 700, padding: '1px 8px', borderRadius: 20,
                textTransform: 'uppercase', letterSpacing: '0.5px',
              }}>
                {client.statut_cli}
              </span>
              {client.fonction && (
                <span style={{ fontSize: 12, color: '#6B7280' }}>{client.fonction}</span>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="header-btn btn-secondary btn-sm"
            onClick={() => window.print()}
            style={{ display: 'flex', alignItems: 'center', gap: 5 }}
            title="Imprimer ou enregistrer en PDF">
            <Printer size={13} /> PDF
          </button>
          <button className="header-btn btn-secondary btn-sm"
            onClick={() => navigate(`/clients/${id}/modifier`)}
            style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Pencil size={13} /> Modifier
          </button>
          {client.statut_cli === 'archive' ? (
            <button className="header-btn btn-secondary btn-sm"
              onClick={handleActiver}
              style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <RotateCcw size={13} /> Réactiver
            </button>
          ) : (
            <button className="header-btn btn-danger btn-sm"
              onClick={handleArchiver}
              style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <UserX size={13} /> Archiver
            </button>
          )}
        </div>
      </div>

      {/* ── Fixed KPI row ── */}
      <div className="no-print" style={{
        display: 'flex', gap: 10, padding: '8px 16px',
        background: '#F9FAFB', borderBottom: '1px solid #E5E7EB', flexShrink: 0,
      }}>
        {[
          { icon: Activity,  label: 'Coachings actifs', value: client.nb_coachings?.actifs ?? 0, color: '#1E7145', bg: '#E8F5E9' },
          { icon: Users,     label: 'Coachings total',  value: client.nb_coachings?.total  ?? 0, color: '#1F4E79', bg: '#EBF3FB' },
          { icon: FileText,  label: 'Documents',        value: client.documents?.length    ?? 0, color: '#ED7D31', bg: '#FFF8E7' },
        ].map(({ icon: Icon, label, value, color, bg }) => (
          <div key={label} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: bg, borderRadius: 8, padding: '6px 14px', minWidth: 130,
          }}>
            <Icon size={15} color={color} />
            <div>
              <div style={{ fontSize: 9, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase' }}>{label}</div>
              <div style={{ fontSize: 17, fontWeight: 800, color, lineHeight: 1.1 }}>{value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Fixed tabs ── */}
      <div className="tabs no-print" style={{ flexShrink: 0, marginBottom: 0 }}>
        {TABS.map(t => (
          <button key={t.id} className={`tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}>
            {t.label}
            {t.id === 'coachings' && client.nb_coachings?.actifs > 0 && (
              <span style={{ marginLeft: 6, background: '#1E7145', color: 'white',
                fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10 }}>
                {client.nb_coachings.actifs}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Scrollable content ── */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '14px 16px' }}>

        {/* ── Fiche client ── */}
        {tab === 'fiche' && (
          <div className="print-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>

            {/* Colonne gauche */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

              <div className="card" style={S.card}>
                <SHead icon={User} title="Identité" />
                <div style={S.grid2}>
                  <Field label="Nom"    value={client.nom_cli} />
                  <Field label="Prénom" value={client.prenom_cli} />
                  {client.nom_jeune_fille && <Field label="Nom jeune fille" value={client.nom_jeune_fille} />}
                  <Field label="Sexe" value={sexeLabel(client.sexe_cli)} />
                  <Field label="Date naissance"
                    value={client.date_naissance_cli
                      ? `${fmt(client.date_naissance_cli)} (${age(client.date_naissance_cli)})`
                      : null} />
                  <Field label="Anniversaire" value={anniversaire(client.date_naissance_cli)} />
                  <Field
                    label={client.type_identite === 'passeport' ? 'N° Passeport' : 'N° CNI'}
                    value={client.numero_identite}
                  />
                </div>
              </div>

              <div className="card" style={S.card}>
                <SHead icon={Phone} title="Coordonnées" />
                <div style={S.grid2}>
                  <Field label="Téléphone" value={client.telephone_principal} />
                  <Field label="Email"     value={client.email_cli} />
                </div>
              </div>

            </div>

            {/* Colonne droite */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

              <div className="card" style={S.card}>
                <SHead icon={Briefcase} title="Profil professionnel" />
                <div style={S.grid2}>
                  <Field label="Fonction"        value={client.fonction} />
                  <Field label="Commercial réf." value={client.commercial_ref_nom} />
                </div>
              </div>

              <div className="card" style={S.card}>
                <SHead icon={Calendar} title="Informations CRM" />
                <div style={S.grid2}>
                  <Field label="Date inscription" value={fmt(client.date_inscription_cli)} />
                  <Field label="Statut"           value={client.statut_cli} />
                  <Field label="Créé le"          value={fmt(client.created_at)} />
                  <Field label="Modifié le"       value={fmt(client.updated_at)} />
                </div>
              </div>

              {client.note_interne && (
                <div className="card" style={S.card}>
                  <SHead icon={FileText} title="Notes internes" />
                  <p style={{ fontSize: 12, color: '#374151', lineHeight: 1.7,
                    whiteSpace: 'pre-wrap', margin: 0 }}>
                    {client.note_interne}
                  </p>
                </div>
              )}

            </div>

            <div className="print-only print-grid-full print-footer">
              <span>NANCOZI CORPORATION — Document confidentiel</span>
              <span>{client.prenom_cli} {client.nom_cli} — Fiche générée le {new Date().toLocaleDateString('fr-FR')}</span>
            </div>
          </div>
        )}

        {/* ── Coachings ── */}
        {tab === 'coachings' && (
          <div className="card">
            {(!client.nb_coachings || client.nb_coachings.total === 0) ? (
              <div style={{ textAlign: 'center', padding: 48, color: '#6B7280' }}>
                <Activity size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
                <p style={{ fontSize: 14 }}>Aucun coaching enregistré pour ce client.</p>
                <button className="header-btn btn-primary"
                  onClick={() => navigate(`/coachings/nouveau?client=${id}`)}
                  style={{ marginTop: 16, fontSize: 13 }}>
                  Créer un coaching
                </button>
              </div>
            ) : (
              <CoachingsTab clientId={id} />
            )}
          </div>
        )}

        {/* ── Documents ── */}
        {tab === 'documents' && (
          <DocumentsTab clientId={id} onRefresh={() => dispatch(fetchClient(id))} />
        )}

        {/* ── Historique ── */}
        {tab === 'historique' && (
          <div>
            {loadingHist ? (
              <div className="spinner" style={{ marginTop: 40 }}>
                <div className="spinner-ring" /> Chargement de l'historique...
              </div>
            ) : historique ? (
              <HistoriqueTab data={historique} />
            ) : null}
          </div>
        )}

      </div>
    </div>
  );
}

/* ── Sous-composant : Documents ── */
function DocumentsTab({ clientId, onRefresh }) {
  const [docs,       setDocs]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showForm,   setShowForm]   = useState(false);
  const [typeDoc,    setTypeDoc]    = useState('');
  const [fichier,    setFichier]    = useState(null);
  const [uploading,  setUploading]  = useState(false);
  const inputRef = useRef(null);

  const charger = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/clients/documents/', { params: { client: clientId } });
      setDocs(data.results || data);
    } catch {
      setDocs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { charger(); }, [clientId]); // eslint-disable-line

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!typeDoc)   { toast.error('Sélectionnez le type de document.');  return; }
    if (!fichier)   { toast.error('Sélectionnez un fichier à téléverser.'); return; }

    setUploading(true);
    try {
      const form = new FormData();
      form.append('client',        clientId);
      form.append('type_document', typeDoc);
      form.append('fichier',       fichier);
      await api.post('/clients/documents/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document téléversé avec succès.');
      setShowForm(false);
      setTypeDoc('');
      setFichier(null);
      if (inputRef.current) inputRef.current.value = '';
      charger();
      onRefresh();
    } catch (err) {
      const msg = err.response?.data
        ? Object.values(err.response.data).flat().join(' | ')
        : 'Erreur lors du téléversement.';
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleSupprimer = async (docId, nom) => {
    if (!window.confirm(`Supprimer le document "${nom}" ?`)) return;
    try {
      await api.delete(`/clients/documents/${docId}/`);
      toast.success('Document supprimé.');
      charger();
      onRefresh();
    } catch {
      toast.error('Erreur lors de la suppression.');
    }
  };

  if (loading) {
    return (
      <div className="spinner" style={{ padding: 40 }}>
        <div className="spinner-ring" /> Chargement des documents...
      </div>
    );
  }

  return (
    <div>
      {/* ── Barre d'action ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <p style={{ fontSize: 13, color: '#6B7280', margin: 0 }}>
          {docs.length} document(s) enregistré(s)
        </p>
        <button
          className="header-btn btn-primary"
          onClick={() => setShowForm(v => !v)}
          style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 700 }}
        >
          <Upload size={14} />
          {showForm ? 'Annuler' : 'Ajouter un document'}
        </button>
      </div>

      {/* ── Formulaire upload ── */}
      {showForm && (
        <div className="card" style={{
          marginBottom: 20, border: '2px solid #90CAF9',
          background: '#F0F7FF',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #BFDBFE' }}>
            <Upload size={15} color="#1F4E79" />
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1F4E79', margin: 0 }}>Nouveau document</h3>
          </div>

          <form onSubmit={handleUpload}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              {/* Type */}
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Type de document <span style={{ color: '#C00000' }}>*</span></label>
                <select
                  className="form-control"
                  value={typeDoc}
                  onChange={e => setTypeDoc(e.target.value)}
                  required
                >
                  <option value="">— Sélectionner —</option>
                  {Object.entries(TYPE_DOCUMENT_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>

              {/* Fichier */}
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Fichier <span style={{ color: '#C00000' }}>*</span></label>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  className="form-control"
                  onChange={e => setFichier(e.target.files[0] || null)}
                  required
                />
                {fichier && (
                  <p style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
                    {fichier.name} — {Math.round(fichier.size / 1024)} Ko
                  </p>
                )}
              </div>
            </div>


            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" className="header-btn btn-secondary"
                onClick={() => setShowForm(false)}
                style={{ fontSize: 13 }}>
                Annuler
              </button>
              <button type="submit" className="header-btn btn-primary" disabled={uploading}
                style={{ fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 7 }}>
                <Upload size={14} />
                {uploading ? 'Téléversement...' : 'Téléverser'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Liste des documents ── */}
      {docs.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 48, color: '#6B7280' }}>
          <FileText size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
          <p style={{ fontSize: 14 }}>Aucun document téléversé pour ce client.</p>
          <p style={{ fontSize: 12, marginTop: 4 }}>
            Cliquez sur <strong>Ajouter un document</strong> pour commencer.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {docs.map(d => (
            <div key={d.id} className="card" style={{
              display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px',
              borderLeft: '4px solid #1F4E79',
            }}>
              <FileText size={22} color="#1F4E79" style={{ flexShrink: 0 }} />

              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#1F2937' }}>
                    {TYPE_DOCUMENT_LABELS[d.type_document] || d.type_document}
                  </span>
                  <span className="badge badge-gris">v{d.version_doc}</span>
                </div>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <span>📄 {d.nom_fichier}</span>
                  <span>📦 {d.taille_ko} Ko</span>
                  <span>📅 {new Date(d.date_upload).toLocaleDateString('fr-FR')}</span>
                  {d.uploade_par_nom && <span>👤 {d.uploade_par_nom}</span>}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                {d.url_fichier && (
                  <a href={d.url_fichier} target="_blank" rel="noreferrer"
                    className="header-btn btn-secondary btn-sm btn-icon"
                    title="Voir le fichier">
                    <Eye size={14} />
                  </a>
                )}
                <button
                  className="header-btn btn-danger btn-sm btn-icon"
                  onClick={() => handleSupprimer(d.id, d.nom_fichier)}
                  title="Supprimer">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Sous-composant : Coachings ── */
function CoachingsTab({ clientId }) {
  const [coachings, setCoachings] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    import('../services/api').then(({ default: api }) => {
      api.get('/coachings/', { params: { client: clientId } })
        .then(r => setCoachings(r.data.results || r.data))
        .catch(() => setCoachings([]));
    });
  }, [clientId]);

  if (!coachings) return (
    <div className="spinner" style={{ padding: 40 }}>
      <div className="spinner-ring" /> Chargement...
    </div>
  );

  const statutIcon = (s) => {
    if (s === 'en_cours')  return <CheckCircle size={14} color="#1E7145" />;
    if (s === 'termine')   return <Clock size={14} color="#6B7280" />;
    return <AlertCircle size={14} color="#ED7D31" />;
  };
  const statutBadge = (s) => {
    const map = { en_cours: 'vert', termine: 'gris', suspendu: 'orange', annule: 'rouge' };
    return <span className={`badge badge-${map[s] || 'gris'}`}>{s.replace('_', ' ')}</span>;
  };

  return (
    <table style={{ width: '100%' }}>
      <thead>
        <tr>
          <th>Contrat</th>
          <th>Statut</th>
          <th>Domaine</th>
          <th>Début</th>
          <th>Fin prévue</th>
          <th>Prix</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {coachings.map((c, i) => (
          <tr key={c.id}>
            <td style={{ fontWeight: 700, color: '#1F4E79', fontSize: 13 }}>
              Coaching {i + 1}
            </td>
            <td style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 14 }}>
              {statutIcon(c.statut)} {statutBadge(c.statut)}
            </td>
            <td>{c.domaine_display || c.domaine || '—'}</td>
            <td style={{ fontSize: 12 }}>{c.date_debut ? new Date(c.date_debut).toLocaleDateString('fr-FR') : '—'}</td>
            <td style={{ fontSize: 12 }}>{c.date_fin   ? new Date(c.date_fin).toLocaleDateString('fr-FR') : '—'}</td>
            <td style={{ fontWeight: 600, color: '#1F4E79' }}>
              {c.prix ? `${Number(c.prix).toLocaleString('fr-FR')} CFA` : '—'}
            </td>
            <td>
              <button className="header-btn btn-secondary btn-sm"
                onClick={() => navigate(`/coachings/${c.id}`)}>
                Voir
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ── Sous-composant : Historique timeline ── */
function HistoriqueTab({ data }) {
  const fmt = (d) => d ? new Date(d).toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric',
  }) : '—';

  const events = [
    ...( data.coachings || []).map(c => ({
      date: c.date_debut, type: 'coaching',
      label: `Coaching ${c.domaine || ''} — ${c.statut}`,
      color: '#1E7145', bg: '#E8F5E9',
    })),
    ...(data.notifications || []).map(n => ({
      date: n.date, type: 'notification',
      label: `Notification ${n.type} (${n.canal}) — ${n.statut}`,
      color: '#2E75B6', bg: '#EBF3FB',
    })),
    ...(data.documents || []).map(d => ({
      date: d.date, type: 'document',
      label: `Document ${d.type} v${d.version}`,
      color: '#ED7D31', bg: '#FFF8E7',
    })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  if (events.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 48, color: '#6B7280' }}>
        <Clock size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
        <p>Aucun historique disponible.</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div style={{ position: 'relative', paddingLeft: 28 }}>
        {/* Ligne verticale */}
        <div style={{ position: 'absolute', left: 10, top: 0, bottom: 0,
          width: 2, background: '#E5E7EB' }} />

        {events.map((ev, i) => (
          <div key={i} style={{ position: 'relative', marginBottom: 20 }}>
            {/* Point */}
            <div style={{
              position: 'absolute', left: -24, top: 4,
              width: 12, height: 12, borderRadius: '50%',
              background: ev.color, border: '2px solid white',
              boxShadow: '0 0 0 2px ' + ev.color,
            }} />
            <div style={{ background: ev.bg, borderRadius: 8, padding: '10px 14px' }}>
              <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 600 }}>
                {fmt(ev.date)}
              </span>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#1F2937', fontWeight: 500 }}>
                {ev.label}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
